import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
  ArrowLeft,
  Bot,
  ClipboardList,
  FlaskConical,
  GitCompare,
  History,
  MoreHorizontal,
  Pencil,
  Play,
  Plug,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { EvalCase } from "@/api/generated/EvalCase"
import type { EvalRunSummary } from "@/api/generated/EvalRunSummary"
import type { EvalSuite } from "@/api/generated/EvalSuite"
import {
  isActiveRun,
  useDeleteCase,
  useDeleteSuite,
  useEvalRuns,
  useEvalSuite,
  useRefreshEvalTools,
  useStartRun,
} from "@/api/queries/evals"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState, PageSkeleton } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CaseSheet } from "@/features/evals/case-sheet"
import { CompareSheet } from "@/features/evals/compare-sheet"
import { formatPassRate, orderPair, passRateTone, runDuration } from "@/features/evals/format"
import { MissingToolLists } from "@/features/evals/missing-tool-lists"
import { PassRateChart } from "@/features/evals/pass-rate-chart"
import { EvalRunSheet } from "@/features/evals/run-sheet"
import { EvalRunStatusBadge } from "@/features/evals/run-status"
import { SuiteDialog } from "@/features/evals/suite-dialog"
import { formatCost, formatDuration } from "@/features/runs/format"
import { fire } from "@/features/studio/fire"

interface SuiteSearch {
  tab?: "runs"
  /** Eval run open in the side sheet. */
  run?: number
  /** Case open in the editor (`new` = create). */
  case?: number | "new"
  /** Compared runs, `"<a>,<b>"`. */
  compare?: string
}

const posInt = (v: unknown) => {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : undefined
}

export const Route = createFileRoute("/_app/studio/evals/$suiteId")({
  staticData: { titleKey: "nav.items.evals" },
  validateSearch: (s: Record<string, unknown>): SuiteSearch => ({
    tab: s.tab === "runs" ? "runs" : undefined,
    run: posInt(s.run),
    case: s.case === "new" ? "new" : posInt(s.case),
    compare: typeof s.compare === "string" && /^\d+,\d+$/.test(s.compare) ? s.compare : undefined,
  }),
  component: SuitePage,
})

function SuitePage() {
  const { t } = useTranslation()
  const { suiteId } = Route.useParams()
  const id = Number(suiteId)
  const suite = useEvalSuite(id)

  if (suite.isPending) return <PageSkeleton />
  if (suite.isError)
    return (
      <PageContainer>
        <ErrorState error={suite.error} onRetry={() => void suite.refetch()} />
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/studio/evals" />}>
            <ArrowLeft />
            {t("studio.evals.backToSuites")}
          </Button>
        </div>
      </PageContainer>
    )
  return <SuiteView suite={suite.data} />
}

function SuiteView({ suite }: { suite: EvalSuite }) {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const runs = useEvalRuns(suite.id)
  const start = useStartRun(suite.id)
  const removeSuite = useDeleteSuite()
  const confirm = useConfirm()
  const [editing, setEditing] = useState(false)
  const setSearch = (patch: Partial<SuiteSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  const tab = search.tab ?? "cases"
  const runList = runs.data?.items ?? []
  const anyActive = runList.some((r) => isActiveRun(r.status))
  const comparePair = search.compare?.split(",").map(Number) as [number, number] | undefined
  const editingCase =
    typeof search.case === "number" ? suite.cases.find((c) => c.id === search.case) : undefined

  const previousOf = (runId: number) => {
    const current = runList.find((r) => r.id === runId)
    if (!current) return undefined
    return runList.find((r) => r.status === "done" && r.id !== runId && r.created_at < current.created_at)?.id
  }

  const refreshTools = useRefreshEvalTools(suite.id)
  const startRun = () => fire(start.mutateAsync().then((r) => setSearch({ tab: "runs", run: r.run_id })))

  return (
    <PageContainer width="wide">
      <PageHeader
        icon={FlaskConical}
        title={suite.name}
        breadcrumbs={[{ label: t("studio.evals.title"), to: "/studio/evals" }, { label: suite.name }]}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <Bot className="size-3.5" />
              {suite.agent_name}
            </span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1" title={t("studio.evals.mcp.mockedHint")}>
              <Plug className="size-3.5" />
              {t("studio.evals.mcp.mocked")}
            </span>
            {suite.description && (
              <>
                <span aria-hidden>·</span>
                <span>{suite.description}</span>
              </>
            )}
          </span>
        }
        badge={
          suite.last_run_pass_rate != null && (
            <StatusBadge tone={passRateTone(suite.last_run_pass_rate)} className="tabular-nums">
              {formatPassRate(suite.last_run_pass_rate)}
            </StatusBadge>
          )
        }
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={<Button variant="outline" size="icon" aria-label={t("studio.evals.suiteActions")} />}
              >
                <MoreHorizontal />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-auto min-w-44">
                <DropdownMenuItem onClick={() => setEditing(true)}>
                  <Pencil />
                  {t("studio.evals.editSuite")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => refreshTools.mutate()} disabled={refreshTools.isPending}>
                  <RefreshCw />
                  {t("studio.evals.mcp.refresh")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() =>
                    void confirm({
                      title: t("studio.evals.deleteSuiteTitle", { name: suite.name }),
                      description: t("studio.evals.deleteSuiteDescription"),
                      confirmLabel: t("studio.evals.deleteSuite"),
                      destructive: true,
                      confirmText: suite.name,
                      onConfirm: async () => {
                        await removeSuite.mutateAsync(suite)
                        void navigate({ to: "/studio/evals" })
                      },
                    })
                  }
                >
                  <Trash2 />
                  {t("studio.evals.deleteSuite")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => setSearch({ case: "new" })} data-testid="add-case">
              <Plus />
              {t("studio.evals.addCase")}
            </Button>
            <Button
              onClick={startRun}
              disabled={start.isPending || suite.cases.length === 0}
              title={suite.cases.length === 0 ? t("studio.evals.needCases") : undefined}
              data-testid="run-suite"
            >
              {start.isPending ? <Spinner /> : <Play />}
              {t("studio.evals.runSuite")}
            </Button>
          </>
        }
      >
        <Tabs value={tab} onValueChange={(v) => setSearch({ tab: v === "runs" ? "runs" : undefined })}>
          <TabsList variant="line" className="h-8">
            <TabsTrigger value="cases" className="px-2">
              <ClipboardList />
              {t("studio.evals.tabs.cases", { count: suite.cases.length })}
            </TabsTrigger>
            <TabsTrigger value="runs" className="px-2" data-testid="tab-runs">
              <History />
              {t("studio.evals.tabs.runs")}
              {anyActive && <Spinner className="size-3" />}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      <MissingToolLists suite={suite} />
      {tab === "cases" ? (
        <CasesTable
          suite={suite}
          onEdit={(c) => setSearch({ case: c.id })}
          onCreate={() => setSearch({ case: "new" })}
        />
      ) : (
        <RunsPanel
          runs={runs}
          onOpen={(r) => setSearch({ run: r })}
          onCompare={(a, b) => setSearch({ compare: `${a},${b}` })}
          onStart={startRun}
          canStart={suite.cases.length > 0 && !start.isPending}
        />
      )}

      <SuiteDialog open={editing} onOpenChange={setEditing} suite={suite} />
      <CaseSheet
        open={search.case === "new" || editingCase != null}
        onOpenChange={(o) => !o && setSearch({ case: undefined })}
        suiteId={suite.id}
        evalCase={search.case === "new" ? null : (editingCase ?? null)}
      />
      <EvalRunSheet
        suite={suite}
        runId={search.run}
        onOpenChange={(o) => !o && setSearch({ run: undefined })}
        onOpenRun={(r) => setSearch({ tab: "runs", run: r })}
        previousRunId={search.run != null ? previousOf(search.run) : undefined}
        onCompare={(a, b) => setSearch({ run: undefined, compare: `${a},${b}` })}
      />
      <CompareSheet
        pair={comparePair}
        runs={runList}
        onChange={(a, b) => setSearch({ compare: `${a},${b}` })}
        onOpenChange={(o) => !o && setSearch({ compare: undefined })}
      />
    </PageContainer>
  )
}

// ---------------------------------------------------------------- cases

function CasesTable({
  suite,
  onEdit,
  onCreate,
}: {
  suite: EvalSuite
  onEdit: (c: EvalCase) => void
  onCreate: () => void
}) {
  const { t } = useTranslation()
  const remove = useDeleteCase(suite.id)
  const confirm = useConfirm()

  const columns = useMemo(
    () =>
      createColumns<EvalCase>((c) => [
        c.accessor("sort_order", {
          header: "#",
          meta: { cellClassName: "w-10" },
          cell: ({ row }) => (
            <span className="text-muted-foreground text-xs tabular-nums">{row.index + 1}</span>
          ),
        }),
        c.accessor("name", {
          header: t("studio.evals.columns.case"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="max-w-lg min-w-48">
              <div className="truncate font-medium">
                {row.original.name || t("studio.evals.caseN", { id: row.original.id })}
              </div>
              <div className="text-muted-foreground line-clamp-2 text-xs whitespace-pre-line">
                {row.original.input_prompt}
              </div>
            </div>
          ),
        }),
        c.display({
          id: "checks",
          header: t("studio.evals.columns.checks"),
          cell: ({ row }) => <CaseChecks c={row.original} />,
        }),
        c.accessor((x) => x.tags.join(" "), {
          id: "tags",
          header: t("studio.evals.columns.tags"),
          cell: ({ row }) => (
            <div className="flex max-w-48 flex-wrap gap-1">
              {row.original.tags.map((tag) => (
                <StatusBadge key={tag} className="font-mono">
                  {tag}
                </StatusBadge>
              ))}
            </div>
          ),
        }),
        c.accessor("timeout_seconds", {
          header: t("studio.evals.columns.timeout"),
          meta: { align: "end" },
          cell: ({ getValue }) => (
            <span className="text-muted-foreground text-xs tabular-nums">
              {formatDuration(getValue() * 1000)}
            </span>
          ),
        }),
      ]),
    [t],
  )

  return (
    <DataTable
      label={t("studio.evals.tabs.cases", { count: suite.cases.length })}
      data={suite.cases}
      columns={columns}
      getRowId={(c) => String(c.id)}
      searchable
      searchPlaceholder={t("studio.evals.searchCases")}
      onRowClick={onEdit}
      pageSize={50}
      rowActions={(c) => (
        <>
          <DropdownMenuItem onClick={() => onEdit(c)}>
            <Pencil />
            {t("common.edit")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() =>
              void confirm({
                title: t("studio.evals.deleteCaseTitle", {
                  name: c.name || t("studio.evals.caseN", { id: c.id }),
                }),
                description: t("studio.evals.deleteCaseDescription"),
                confirmLabel: t("studio.evals.deleteCase"),
                destructive: true,
                onConfirm: () => remove.mutateAsync(c),
              })
            }
          >
            <Trash2 />
            {t("studio.evals.deleteCase")}
          </DropdownMenuItem>
        </>
      )}
      emptyState={
        <EmptyState
          variant="plain"
          icon={ClipboardList}
          title={t("studio.evals.casesEmpty.title")}
          description={t("studio.evals.casesEmpty.description")}
          action={
            <Button size="sm" onClick={onCreate}>
              <Plus />
              {t("studio.evals.addCase")}
            </Button>
          }
        />
      }
    />
  )
}

function CaseChecks({ c }: { c: EvalCase }) {
  const { t } = useTranslation()
  const chips: string[] = []
  if (c.expected_contains.length)
    chips.push(t("studio.evals.checks.contains", { count: c.expected_contains.length }))
  if (c.expected_not_contains.length)
    chips.push(t("studio.evals.checks.notContains", { count: c.expected_not_contains.length }))
  if (c.expected_regex) chips.push(t("studio.evals.checks.regex"))
  if (c.llm_judge_prompt) chips.push(t("studio.evals.checks.judge", { threshold: c.llm_judge_threshold }))
  if (c.expect_reply != null)
    chips.push(t(c.expect_reply ? "studio.evals.checks.replies" : "studio.evals.checks.silent"))
  if (c.expected_tool_calls.length)
    chips.push(t("studio.evals.checks.toolCalls", { count: c.expected_tool_calls.length }))
  if (c.forbidden_tool_calls.length)
    chips.push(t("studio.evals.checks.forbiddenCalls", { count: c.forbidden_tool_calls.length }))
  return (
    <div className="flex max-w-64 min-w-32 flex-wrap gap-1">
      {chips.length === 0 ? (
        <span className="text-muted-foreground text-xs">{t("studio.evals.checks.none")}</span>
      ) : (
        chips.map((x) => (
          <StatusBadge key={x} tone="accent">
            {x}
          </StatusBadge>
        ))
      )}
    </div>
  )
}

// ---------------------------------------------------------------- runs

function RunsPanel({
  runs,
  onOpen,
  onCompare,
  onStart,
  canStart,
}: {
  runs: ReturnType<typeof useEvalRuns>
  onOpen: (id: number) => void
  onCompare: (a: number, b: number) => void
  onStart: () => void
  canStart: boolean
}) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<number[]>([])
  const list = runs.data?.items ?? []
  const toggle = (id: number, on: boolean) =>
    setSelected((cur) => (on ? [...cur.filter((x) => x !== id), id].slice(-2) : cur.filter((x) => x !== id)))

  const compareSelected = () => {
    const [a, b] = selected.map((id) => list.find((r) => r.id === id)).filter(Boolean) as EvalRunSummary[]
    if (!a || !b) return
    const [x, y] = orderPair(a, b)
    onCompare(x.id, y.id)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: toggle/selected are read through the closure on render
  const columns = useMemo(
    () =>
      createColumns<EvalRunSummary>((c) => [
        c.display({
          id: "select",
          header: () => <span className="sr-only">{t("studio.evals.selectForCompare")}</span>,
          meta: { cellClassName: "w-8" },
          cell: ({ row }) => (
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selected.includes(row.original.id)}
                disabled={isActiveRun(row.original.status)}
                onCheckedChange={(v) => toggle(row.original.id, !!v)}
                aria-label={t("studio.evals.selectRun", { id: row.original.id })}
              />
            </div>
          ),
        }),
        c.accessor("id", {
          header: t("studio.evals.columns.run"),
          cell: ({ row }) => (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[0.8125rem]">#{row.original.id}</span>
              {row.original.source_run_id != null && (
                <StatusBadge>{t("studio.evals.rerunOf", { id: row.original.source_run_id })}</StatusBadge>
              )}
            </div>
          ),
        }),
        c.accessor("status", {
          header: t("studio.evals.columns.status"),
          cell: ({ getValue }) => <EvalRunStatusBadge status={getValue()} />,
        }),
        c.accessor("pass_rate", {
          header: t("studio.evals.columns.passRate"),
          meta: { align: "end" },
          cell: ({ getValue }) =>
            getValue() == null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <StatusBadge tone={passRateTone(getValue())} className="tabular-nums">
                {formatPassRate(getValue())}
              </StatusBadge>
            ),
        }),
        c.display({
          id: "results",
          header: t("studio.evals.columns.results"),
          cell: ({ row }) => {
            const r = row.original
            return (
              <span className="flex items-center gap-2 text-xs tabular-nums">
                <span className="text-success">{t("studio.evals.passCount", { count: r.pass_count })}</span>
                <span className="text-destructive">
                  {t("studio.evals.failCount", { count: r.fail_count })}
                </span>
                {r.error_count > 0 && (
                  <span className="text-warning">
                    {t("studio.evals.errorCount", { count: r.error_count })}
                  </span>
                )}
                <span className="text-muted-foreground">/ {r.case_count}</span>
              </span>
            )
          },
        }),
        c.accessor("total_cost", {
          header: t("studio.evals.columns.cost"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="text-xs tabular-nums">{formatCost(getValue())}</span>,
        }),
        c.display({
          id: "duration",
          header: t("studio.evals.columns.duration"),
          meta: { align: "end" },
          cell: ({ row }) => (
            <span className="text-muted-foreground text-xs tabular-nums">
              {formatDuration(runDuration(row.original))}
            </span>
          ),
        }),
        c.accessor("created_at", {
          header: t("studio.evals.columns.started"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t, selected],
  )

  if (runs.isError) return <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />

  return (
    <div className="space-y-4">
      <PassRateChart runs={list} onSelect={onOpen} />
      <DataTable
        label={t("studio.evals.tabs.runs")}
        data={runs.data?.items}
        columns={columns}
        getRowId={(r) => String(r.id)}
        loading={runs.isPending}
        onRowClick={(r) => onOpen(r.id)}
        initialSorting={[{ id: "created_at", desc: true }]}
        toolbar={
          <span className="text-muted-foreground text-xs">
            {selected.length === 2 ? t("studio.evals.twoSelected") : t("studio.evals.selectTwo")}
          </span>
        }
        toolbarEnd={
          <Button
            size="sm"
            variant="outline"
            disabled={selected.length !== 2}
            onClick={compareSelected}
            data-testid="compare-selected"
          >
            <GitCompare />
            {t("studio.evals.compareSelected")}
          </Button>
        }
        emptyState={
          <EmptyState
            variant="plain"
            icon={History}
            title={t("studio.evals.runsEmpty.title")}
            description={t("studio.evals.runsEmpty.description")}
            action={
              <Button size="sm" onClick={onStart} disabled={!canStart}>
                <Play />
                {t("studio.evals.runSuite")}
              </Button>
            }
          />
        }
      />
    </div>
  )
}
